import { Turnstile } from '@marsidev/react-turnstile'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { NoIndex } from '../../components/NoIndex'
import { publicApi, ApiError } from '../../lib/api'
import { ThemeProvider } from '../../lib/theme'

type Intent = 'cash' | 'self_buy'
type PaymentMethod = 'iban' | 'lydia' | 'wero' | 'paypal' | 'direct'

function fmtEuro(cents: number) {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

function isEmail(value: string) {
  const v = value.trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
}

/** Preview/E2E: omit Turnstile when API runs in preview; set `VITE_E2E_SKIP_TURNSTILE=1` on Pages preview builds used by CI. */
const skipClientTurnstile = import.meta.env.DEV || import.meta.env.VITE_E2E_SKIP_TURNSTILE === '1'

export default function ContributeForm() {
  const { slug, itemId } = useParams<{ slug: string; itemId: string }>()
  const nav = useNavigate()

  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [catalog, setCatalog] = useState<Awaited<ReturnType<typeof publicApi.catalog>> | null>(null)

  const [intent, setIntent] = useState<Intent>('cash')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [amountCents, setAmountCents] = useState<number>(0)
  const [method, setMethod] = useState<PaymentMethod>('direct')
  const [message, setMessage] = useState('')
  const [confirm, setConfirm] = useState(false)

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<{ guestName: string; complete: boolean } | null>(null)

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    publicApi
      .catalog(slug)
      .then((data) => {
        if (cancelled) return
        setCatalog(data)
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setLoading(false)
        if (e instanceof ApiError && e.status === 404) setNotFound(true)
        else setNotFound(true)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const item = useMemo(() => {
    if (!catalog || !itemId) return null
    return catalog.items.find((it) => it.id === itemId) ?? null
  }, [catalog, itemId])

  const registry = catalog?.registry ?? null

  const availableMethods = useMemo(() => {
    const pm = registry?.paymentMethods
    const list: Array<{ id: PaymentMethod; label: string; detail: string | null }> = [
      { id: 'direct', label: 'Direct', detail: 'En main propre' },
    ]
    if (pm?.iban)
      list.unshift({
        id: 'iban',
        label: 'IBAN',
        detail: pm.ibanHolder ? `${pm.ibanHolder} · ${pm.iban}` : pm.iban,
      })
    if (pm?.lydiaPhone) list.push({ id: 'lydia', label: 'Lydia', detail: pm.lydiaPhone })
    if (pm?.weroPhone) list.push({ id: 'wero', label: 'Wero', detail: pm.weroPhone })
    if (pm?.paypalEmail) list.push({ id: 'paypal', label: 'PayPal', detail: pm.paypalEmail })
    return list
  }, [registry])

  useEffect(() => {
    if (!item) return
    setAmountCents(item.remainingCents)
    setMethod(availableMethods[0]?.id ?? 'direct')
  }, [item, availableMethods])

  if (loading) {
    return (
      <main className="page guest-shell">
        <NoIndex />
        <div className="guest-boot">
          <div className="guest-boot-title">Chargement…</div>
        </div>
      </main>
    )
  }

  if (notFound || !catalog || !registry || !slug || !itemId) {
    return (
      <main className="page guest-shell">
        <NoIndex />
        <div className="guest-boot">
          <div className="guest-boot-title">Introuvable</div>
          <p className="guest-boot-sub">Cette liste (ou cet item) n’existe pas.</p>
          <Link className="btn btn-secondary" to={slug ? `/r/${slug}` : '/'}>
            Retour
          </Link>
        </div>
      </main>
    )
  }

  if (!item) {
    return (
      <ThemeProvider theme={registry.theme} accentColor={registry.accentColor}>
        <main className="page guest-shell">
          <NoIndex />
          <div className="guest-boot">
            <div className="guest-boot-title">Cet item n’existe plus</div>
            <Link className="btn btn-primary" to={`/r/${slug}`}>
              Retour à la liste
            </Link>
          </div>
        </main>
      </ThemeProvider>
    )
  }

  if (item.complete || item.offered) {
    return (
      <ThemeProvider theme={registry.theme} accentColor={registry.accentColor}>
        <main className="page guest-shell">
          <NoIndex />
          <div className="guest-catalog">
            <Link className="guest-back" to={`/r/${slug}`}>
              ← Retour à la liste
            </Link>
            <div className="guest-product">
              <div className="guest-product-img">
                {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <div className="guest-ph" />}
                <div className="guest-ribbon">
                  <span>Déjà offert</span>
                </div>
              </div>
              <div className="guest-product-meta">
                <p className="guest-eyebrow">Déjà offert</p>
                <h1 className="guest-product-title">{item.title}</h1>
                <p className="muted">Cet item a déjà été offert. Merci pour votre présence.</p>
                <Link className="btn btn-primary" to={`/r/${slug}`}>
                  Retour à la liste
                </Link>
              </div>
            </div>
          </div>
        </main>
      </ThemeProvider>
    )
  }

  if (success) {
    return (
      <ThemeProvider theme={registry.theme} accentColor={registry.accentColor}>
        <main className="page guest-shell">
          <NoIndex />
          <div className="guest-catalog">
            <div className="guest-thanks">
              <div className="guest-conf-spark" aria-hidden="true">
                ✨
              </div>
              <p className="guest-eyebrow">Participation enregistrée</p>
              <h1 className="guest-h1">Merci {success.guestName}</h1>
              <p className="guest-thanks-lead">
                Vous allez recevoir un email avec les instructions.
                {success.complete ? ' Cet item est maintenant complet.' : null}
              </p>
              <div className="guest-ornament" aria-hidden="true">
                <span className="guest-dot" />
                <span className="guest-line" />
                <span className="guest-dot" />
              </div>
              <button className="btn btn-primary" onClick={() => nav(`/r/${slug}`)}>
                Retour à la liste
              </button>
            </div>
          </div>
        </main>
      </ThemeProvider>
    )
  }

  const remainingCents = item.remainingCents
  const pct =
    item.priceCents > 0
      ? Math.min(100, Math.round((item.alreadyGivenCents / item.priceCents) * 100))
      : 0
  const moneyMethods = availableMethods.filter((m) => m.id !== 'direct')

  const effectiveMethod: PaymentMethod = intent === 'self_buy' ? 'direct' : method
  const effectiveAmount =
    intent === 'self_buy' ? remainingCents : Math.max(100, Math.min(remainingCents, amountCents))

  const canSubmit =
    !submitting &&
    name.trim().length > 0 &&
    isEmail(email) &&
    effectiveAmount >= 100 &&
    confirm &&
    (skipClientTurnstile || !!turnstileToken)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    setInfo(null)

    const errs: string[] = []
    if (!name.trim()) errs.push('Indiquez votre nom.')
    if (!isEmail(email)) errs.push('L’email ne semble pas valide.')
    if (!confirm)
      errs.push(
        intent === 'cash'
          ? 'Confirmez d’abord avoir effectué le paiement.'
          : 'Confirmez votre engagement.',
      )
    if (intent === 'cash' && effectiveAmount < 100) errs.push('Le montant doit être au moins 1 €.')
    if (!skipClientTurnstile && !turnstileToken) errs.push('Validation anti-spam requise.')
    if (errs.length) {
      setSubmitError(errs.join(' '))
      return
    }

    setSubmitting(true)
    try {
      const res = await publicApi.contribute(slug!, {
        itemId: itemId!,
        guestName: name.trim(),
        guestEmail: email.trim(),
        amountCents: effectiveAmount,
        message: message.trim() ? message.trim() : null,
        paymentMethod: effectiveMethod,
        turnstileToken: turnstileToken ?? undefined,
      })
      if ('ok' in res && res.ok) {
        setSuccess({ guestName: name.trim(), complete: res.complete })
        return
      }
      if ('error' in res && res.error === 'insufficient_remaining') {
        setInfo(
          `Un autre invité vient de participer. Le restant est maintenant ${fmtEuro(res.remainingCents)}.`,
        )
        setAmountCents(Math.min(amountCents, res.remainingCents))
        return
      }
      if ('error' in res && res.error === 'turnstile_failed') {
        setSubmitError('Validation anti-spam échouée. Réessayez.')
        return
      }
      setSubmitError('Erreur lors de l’envoi. Réessayez dans un instant.')
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && isItemOfferedError(err.body)) {
        setSubmitError(
          'Cet objet vient d’être marqué comme déjà offert. Merci, il n’est plus à offrir.',
        )
      } else if (err instanceof ApiError && err.status === 409) {
        const nextRemaining = getRemainingCentsFromError(err.body) ?? remainingCents
        setInfo(
          `Un autre invité vient de participer. Le restant est maintenant ${fmtEuro(nextRemaining)}.`,
        )
        setAmountCents(Math.min(amountCents, nextRemaining))
      } else {
        setSubmitError('Erreur réseau. Réessayez dans un instant.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ThemeProvider theme={registry.theme} accentColor={registry.accentColor}>
      <main className="page guest-shell">
        <NoIndex />
        <div className="guest-catalog">
          <Link className="guest-back" to={`/r/${slug}`}>
            ← Retour à la liste
          </Link>

          <form className="guest-product" onSubmit={onSubmit}>
            <div className="guest-product-img">
              {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <div className="guest-ph" />}
            </div>

            <div className="guest-product-meta">
              <p className="guest-eyebrow">Participer pour</p>
              <h1 className="guest-product-title">{item.title}</h1>
              {item.description ? <p className="guest-product-desc">{item.description}</p> : null}

              <div className="guest-product-progress">
                <div className="guest-product-prices">
                  <span>
                    <strong>{fmtEuro(item.alreadyGivenCents)}</strong>{' '}
                    <span className="muted">offerts sur</span>{' '}
                    <strong>{fmtEuro(item.priceCents)}</strong>
                  </span>
                  <span className="muted">
                    reste{' '}
                    <strong style={{ color: 'var(--accent)' }}>{fmtEuro(remainingCents)}</strong>
                  </span>
                </div>
                <div className="guest-prog" aria-hidden="true">
                  <div className="guest-prog-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {item.externalUrl ? (
                <a
                  className="guest-card-link"
                  href={item.outUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  voir le produit ↗
                </a>
              ) : null}

              <div className="guest-divider">
                <span>Comment participer</span>
              </div>

              <fieldset className="guest-fieldset">
                <legend>Que souhaitez-vous faire ?</legend>
                <div className="guest-intent">
                  <label className={`guest-intent-opt ${intent === 'cash' ? 'is-on' : ''}`}>
                    <input
                      type="radio"
                      name="intent"
                      checked={intent === 'cash'}
                      onChange={() => {
                        setIntent('cash')
                        setConfirm(false)
                      }}
                    />
                    <span className="guest-intent-title">Participer financièrement</span>
                    <span className="guest-intent-sub">
                      Vous offrez un montant, les parents achètent l’objet.
                    </span>
                  </label>
                  <label className={`guest-intent-opt ${intent === 'self_buy' ? 'is-on' : ''}`}>
                    <input
                      type="radio"
                      name="intent"
                      checked={intent === 'self_buy'}
                      onChange={() => {
                        setIntent('self_buy')
                        setConfirm(false)
                      }}
                    />
                    <span className="guest-intent-title">J’achète moi-même l’objet</span>
                    <span className="guest-intent-sub">
                      Vous réservez l’objet entier et l’offrez en personne.
                    </span>
                  </label>
                </div>
              </fieldset>

              {intent === 'cash' ? (
                <div className="guest-money">
                  <div className="guest-row2">
                    <label className="guest-field">
                      <span className="guest-lbl">Montant (€)</span>
                      <input
                        className="input"
                        inputMode="decimal"
                        value={(amountCents / 100).toFixed(2).replace('.', ',')}
                        onChange={(e) => {
                          const raw = e.target.value.replace(',', '.')
                          const parsed = Number.parseFloat(raw)
                          if (Number.isNaN(parsed)) return setAmountCents(0)
                          setAmountCents(Math.round(parsed * 100))
                        }}
                      />
                      <p className="field-hint">Vous pouvez donner moins ou tout payer.</p>
                    </label>
                    <label className="guest-field">
                      <span className="guest-lbl">Mode de paiement</span>
                      <select
                        className="input"
                        value={method}
                        onChange={(e) => {
                          setMethod(e.target.value as PaymentMethod)
                          setConfirm(false)
                        }}
                      >
                        {moneyMethods.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label}
                          </option>
                        ))}
                        <option value="direct">Direct</option>
                      </select>
                      <p className="field-hint">
                        {availableMethods.find((m) => m.id === method)?.detail ?? ''}
                      </p>
                    </label>
                  </div>

                  <PaymentHelp
                    method={method}
                    amountCents={effectiveAmount}
                    registry={registry}
                    guestName={name.trim()}
                  />
                </div>
              ) : (
                <div className="guest-payblock">
                  <p className="guest-pay-eyebrow">Vous achetez l’objet</p>
                  <p className="guest-pay-intro">
                    En confirmant, vous réservez <strong>{item.title}</strong> dans la liste — il ne
                    sera plus visible pour les autres invités.
                  </p>
                  <ul className="guest-self-steps">
                    <li>Vous achetez l’objet quand ça vous arrange.</li>
                    <li>Vous l’offrez en personne le jour J (ou vous convenez d’un moment).</li>
                  </ul>
                </div>
              )}

              <div className="guest-row2">
                <label className="guest-field">
                  <span className="guest-lbl">Votre nom</span>
                  <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="guest-field">
                  <span className="guest-lbl">Votre email</span>
                  <input
                    className="input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
              </div>

              <label className="guest-field">
                <span className="guest-lbl">
                  Mot pour les parents <span className="muted">(optionnel)</span>
                </span>
                <textarea
                  className="input guest-textarea"
                  rows={3}
                  maxLength={2000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </label>

              {skipClientTurnstile ? null : (
                <div className="guest-turnstile">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                    options={{ theme: 'light' }}
                  />
                </div>
              )}

              <label className={`guest-confirm ${confirm ? 'is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                />
                <span className="guest-confirm-box" aria-hidden="true">
                  {confirm ? '✓' : ''}
                </span>
                <span className="guest-confirm-label">
                  {intent === 'cash' ? (
                    <>
                      <strong>J’ai effectué le paiement</strong>
                      <span className="muted"> — on vous fait confiance.</span>
                    </>
                  ) : (
                    <>
                      <strong>Je m’engage à offrir cet objet</strong>
                      <span className="muted"> — il sera réservé pour vous.</span>
                    </>
                  )}
                </span>
              </label>

              {submitError ? <div className="guest-errors">{submitError}</div> : null}
              {info ? <div className="guest-info">{info}</div> : null}

              <div className="guest-foot">
                <button type="button" className="btn btn-ghost" onClick={() => nav(`/r/${slug}`)}>
                  Annuler
                </button>
                <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
                  {submitting
                    ? 'Un instant…'
                    : intent === 'self_buy'
                      ? 'Réserver cet objet'
                      : 'Confirmer'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </ThemeProvider>
  )
}

function isItemOfferedError(body: unknown): boolean {
  return (
    !!body &&
    typeof body === 'object' &&
    'error' in body &&
    (body as { error?: unknown }).error === 'item_offered'
  )
}

function getRemainingCentsFromError(body: unknown): number | null {
  if (!body || typeof body !== 'object') return null
  if (!('remainingCents' in body)) return null
  const value = (body as { remainingCents?: unknown }).remainingCents
  return typeof value === 'number' ? value : null
}

function PaymentHelp({
  method,
  amountCents,
  registry,
  guestName,
}: {
  method: PaymentMethod
  amountCents: number
  registry: NonNullable<Awaited<ReturnType<typeof publicApi.catalog>>['registry']>
  guestName: string
}) {
  const pm = registry.paymentMethods
  const reference = guestName ? `Liste ${guestName}` : 'Liste (votre nom)'

  if (method === 'iban' && pm.iban) {
    return (
      <div className="guest-payblock">
        <p className="guest-pay-eyebrow">Comment payer — IBAN</p>
        <p className="guest-pay-intro">
          Effectuez un virement de <strong>{fmtEuro(amountCents)}</strong> avec les coordonnées
          suivantes :
        </p>
        <div className="guest-kv">
          <span className="guest-k">Titulaire</span>
          <span className="guest-v">{pm.ibanHolder ?? '—'}</span>
        </div>
        <div className="guest-kv">
          <span className="guest-k">IBAN</span>
          <span className="guest-v guest-mono">{pm.iban}</span>
        </div>
        <div className="guest-kv">
          <span className="guest-k">BIC</span>
          <span className="guest-v guest-mono">{pm.bic ?? '—'}</span>
        </div>
        <div className="guest-kv">
          <span className="guest-k">Référence</span>
          <span className="guest-v">
            <em>{reference}</em>
          </span>
        </div>
      </div>
    )
  }

  if (method === 'lydia' && pm.lydiaPhone) {
    return (
      <div className="guest-payblock">
        <p className="guest-pay-eyebrow">Comment payer — Lydia</p>
        <p className="guest-pay-intro">
          Envoyez <strong>{fmtEuro(amountCents)}</strong> via Lydia au{' '}
          <a className="guest-tel" href={`tel:${pm.lydiaPhone.replace(/\s/g, '')}`}>
            {pm.lydiaPhone}
          </a>
          .
        </p>
      </div>
    )
  }

  if (method === 'wero' && pm.weroPhone) {
    return (
      <div className="guest-payblock">
        <p className="guest-pay-eyebrow">Comment payer — Wero</p>
        <p className="guest-pay-intro">
          Envoyez <strong>{fmtEuro(amountCents)}</strong> via Wero au{' '}
          <a className="guest-tel" href={`tel:${pm.weroPhone.replace(/\s/g, '')}`}>
            {pm.weroPhone}
          </a>
          .
        </p>
      </div>
    )
  }

  if (method === 'paypal' && pm.paypalEmail) {
    return (
      <div className="guest-payblock">
        <p className="guest-pay-eyebrow">Comment payer — PayPal</p>
        <p className="guest-pay-intro">
          Envoyez <strong>{fmtEuro(amountCents)}</strong> via PayPal à{' '}
          <a className="guest-tel" href={`mailto:${pm.paypalEmail}`}>
            {pm.paypalEmail}
          </a>
          . <span className="muted">Choisissez l’option « Entre amis » si possible.</span>
        </p>
      </div>
    )
  }

  return (
    <div className="guest-payblock">
      <p className="guest-pay-eyebrow">Direct</p>
      <p className="guest-pay-intro">Vous pourrez offrir ce montant en main propre.</p>
    </div>
  )
}
